import { ShopPlugin } from '@pdqdev/pdq-shared/dist/interfaces';
import { resolveShopPluginCdn } from '@app/pages/checkout-app/utils';
import { v4 as uuidv4 } from 'uuid';

const cdn = resolveShopPluginCdn();

/** CDN URL for a preset icon. Colored variant by default; pass `colored: false` for the plain outline. */
const buildIconUrl = (name: string, options?: { colored?: boolean }): string => {
  const suffix = options?.colored === false ? '' : '-color';
  return `${cdn}/icons/${name}${suffix}.svg`;
};

export const LABEL_PRESET_ICON_NAMES = ['delivery-parcel', 'truck', 'calendar', 'location', 'shield', 'box'] as const;
export type LabelPresetIconName = (typeof LABEL_PRESET_ICON_NAMES)[number];

export const LABEL_PRESET_ICON_URLS = Object.fromEntries(
  LABEL_PRESET_ICON_NAMES.map((name) => [name, buildIconUrl(name)]),
) as Record<LabelPresetIconName, string>;

export const DEFAULT_ICON_URL = LABEL_PRESET_ICON_URLS['delivery-parcel'];

export const DEFAULT_LABEL_OPTIONS: ShopPlugin.ProductPagePromise.LabelOptions = {
  text: 'Estimated delivery',
  iconUrl: DEFAULT_ICON_URL,
};

export const DEFAULT_DELIVERY_DATE_OPTIONS: ShopPlugin.ProductPagePromise.DeliveryDateOptions = {
  calculationMethod: ShopPlugin.ProductPagePromise.DeliveryDateCalculationMethod.FASTEST,
  displayMessage: 'Arrives by {arrival_date}',
  promiseFormat: ShopPlugin.ProductPagePromise.DeliveryDatePromiseFormat.JUST_DATE,
  fallbackMessage: 'No expedited shipping options to your location',
  hideIfLongDelivery: true,
  hideIfLongDeliveryDaysThreshold: 5,
};

export const DEFAULT_SHIPPING_METHOD_LINE_OPTIONS: ShopPlugin.ProductPagePromise.ShippingMethodLineOptions = {
  priceDisplay: ShopPlugin.ProductPagePromise.ShippingMethodLinePriceDisplay.ABSOLUTE_IN_BRACKETS,
  allowAllMethods: false,
  allowedMethodNames: [],
  allowShopperToChangeMethod: true,
  buttonLabel: 'Change',
};

export const DEFAULT_LOCATION_ERROR_MESSAGE = "We couldn't find what you're looking for, please try again";
export const DEFAULT_LOCATION_ERROR_MESSAGE_COLOR = '#000000';

export const DEFAULT_LOCATION_INPUT_OPTIONS: ShopPlugin.ProductPagePromise.LocationInputOptions = {
  type: ShopPlugin.ProductPagePromise.LocationInputType.FULL_ADDRESS,
  emptyStateDescription: 'Enter your location for an exact date',
  fieldPlaceholder: 'Enter your address',
  buttonLabel: 'Check shipping',
  buttonColor: '#000000',
  changeAddressLinkText: 'Change',
  deliveringToText: 'Delivering to',
  autoDetect: false,
  errorMessage: DEFAULT_LOCATION_ERROR_MESSAGE,
  errorMessageColor: DEFAULT_LOCATION_ERROR_MESSAGE_COLOR,
};

export const CUTOFF_TIMER_PRESET_ICON_NAMES = ['clock', 'lightning', 'fire', 'hourglass', 'target'] as const;
export type CutoffTimerPresetIconName = (typeof CUTOFF_TIMER_PRESET_ICON_NAMES)[number];

export const CUTOFF_TIMER_PRESET_ICON_URLS = Object.fromEntries(
  CUTOFF_TIMER_PRESET_ICON_NAMES.map((name) => [name, buildIconUrl(name)]),
) as Record<CutoffTimerPresetIconName, string>;

export const DEFAULT_CUTOFF_TIMER_OPTIONS: ShopPlugin.ProductPagePromise.CutoffTimerOptions = {
  position: ShopPlugin.ProductPagePromise.CutoffTimerPosition.LINKED,
  displayMessage: 'Order within {time_left}',
  iconUrl: CUTOFF_TIMER_PRESET_ICON_URLS['clock'],
  customIconUrl: '',
  backgroundColor: '#eafbea',
};

export const TRUST_SIGNAL_PRESET_ICON_NAMES = ['checkmark', 'dot'] as const;
export type TrustSignalPresetIconName = (typeof TRUST_SIGNAL_PRESET_ICON_NAMES)[number];

export const TRUST_SIGNAL_PRESET_ICON_URLS = Object.fromEntries(
  TRUST_SIGNAL_PRESET_ICON_NAMES.map((name) => [name, buildIconUrl(name)]),
) as Record<TrustSignalPresetIconName, string>;

export const TRUST_SIGNALS_MIN_COUNT = 1;
export const TRUST_SIGNALS_MAX_COUNT = 10;

export const DEFAULT_TRUST_SIGNALS_OPTIONS: ShopPlugin.ProductPagePromise.TrustSignalsOptions = {
  signals: [],
};

export function createDefaultTrustSignal(): ShopPlugin.ProductPagePromise.TrustSignal {
  return {
    id: uuidv4(),
    text: 'Free returns within 30 days',
    iconUrl: TRUST_SIGNAL_PRESET_ICON_URLS['checkmark'],
    customIconUrl: '',
  };
}
